//based on Downshift Combobox example: https://www.downshift-js.com/use-combobox

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCombobox } from 'downshift';

type Bill = {
  id: string;
  title?: string;
  description?: string;
  keywords?: string[];
  congress?: number;
};

export default function BillCombo({
  items: initialItems,
  setBillId,
  billId,
  recommendedBillId,
}: {
  items: Bill[];
  setBillId: (id: string) => void;
  billId?: string;
  recommendedBillId?: string | null;
}) {
  const [items, setItems] = useState<Bill[]>(initialItems || []);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{
    top: number;
    left: number;
    width: number;
    maxWidth?: number;
  } | null>(null);

  // Organize items into recommended and other sections
  const organizeItems = (allItems: Bill[]) => {
    const recommended = allItems.filter(
      (b) => recommendedBillId && b.id.toLowerCase() === recommendedBillId.toLowerCase(),
    );
    const other = allItems.filter(
      (b) => !recommendedBillId || b.id.toLowerCase() !== recommendedBillId.toLowerCase(),
    );
    // Return flat array with recommended first for downshift to manage
    return [...recommended, ...other];
  };

  useEffect(() => {
    setItems(organizeItems(initialItems || []));
  }, [initialItems, recommendedBillId]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
  } = useCombobox<Bill>({
    items,
    itemToString: (item) =>
      item ? `${item.id.toUpperCase()} - ${item.title || '(untitled)'}` : '',
    onInputValueChange: ({ inputValue }) => {
      const iv = inputValue || '';
      const lower = iv.toLowerCase();
      const filtered = (initialItems || []).filter((b) => {
        return (
          !iv ||
          (b.id && b.id.toLowerCase().includes(lower)) ||
          (b.title && b.title.toLowerCase().includes(lower)) ||
          (b.description && b.description.toLowerCase().includes(lower)) ||
          (b.keywords && b.keywords.some((k) => k.toLowerCase().includes(lower)))
        );
      });
      setItems(organizeItems(filtered));
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) setBillId(selectedItem.id);
    },
  });

  useEffect(() => {
    function update() {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = rect.width;
      setMenuStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width,
        maxWidth: width,
      });
    }

    if (isOpen) update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, items.length]);

  useEffect(() => {
    if (!billId) return;
    const found = (initialItems || []).find((b) => b.id === billId);
    if (found) setInputValue(`${found.id.toUpperCase()} - ${found.title || '(untitled)'}`);
  }, [billId, initialItems, setInputValue]);

  const menu = (
    <ul
      {...getMenuProps()}
      className={`list-unstyled position-absolute bg-white shadow-sm mt-1 p-0 ${!(isOpen && items.length) && 'd-none'}`}
      style={{
        position: 'absolute',
        top: menuStyle ? `${menuStyle.top}px` : undefined,
        left: menuStyle ? `${menuStyle.left}px` : undefined,
        width: menuStyle ? `${menuStyle.width}px` : undefined,
        maxWidth: menuStyle && menuStyle.maxWidth ? `${menuStyle.maxWidth}px` : undefined,
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        zIndex: 1050,
      }}
    >
      {isOpen && items.length > 0 && (
        <>
          {/* Find the index where "Other" section starts */}
          {(() => {
            const recommendedCount = items.filter(
              (b) => recommendedBillId && b.id.toLowerCase() === recommendedBillId.toLowerCase(),
            ).length;
            const hasRecommended = recommendedCount > 0;

            return (
              <>
                {hasRecommended && (
                  <>
                    <li
                      className="py-2 px-3 fw-bold text-muted bg-light"
                      style={{ fontSize: '0.75rem', cursor: 'default' }}
                    >
                      Recommended
                    </li>
                    {items.slice(0, recommendedCount).map((item, index) => (
                      <li
                        key={item.id}
                        {...getItemProps({ item, index })}
                        className={`py-0 px-3 ${
                          highlightedIndex === index ? 'bg-primary text-white' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="small">
                          {item.id.toUpperCase()} - {item.title || '(untitled)'}
                        </div>
                      </li>
                    ))}
                  </>
                )}

                {items.length > recommendedCount && (
                  <>
                    <li
                      className="py-2 px-3 fw-bold text-muted bg-light"
                      style={{ fontSize: '0.75rem', cursor: 'default' }}
                    >
                      Other
                    </li>
                    {items.slice(recommendedCount).map((item, index) => {
                      const actualIndex = recommendedCount + index;
                      return (
                        <li
                          key={item.id}
                          {...getItemProps({ item, index: actualIndex })}
                          className={`py-0 px-3 ${
                            highlightedIndex === actualIndex ? 'bg-primary text-white' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="small">
                            {item.id.toUpperCase()} - {item.title || '(untitled)'}
                          </div>
                        </li>
                      );
                    })}
                  </>
                )}
              </>
            );
          })()}
        </>
      )}
    </ul>
  );

  return (
    <div style={{ flex: '1 1 auto' }} ref={containerRef}>
      <div className="d-flex">
        <input
          className="form-control form-control-sm"
          placeholder="Please enter or select a value"
          {...getInputProps({ id: 'toolbar-bill' })}
        />

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm ms-1 text-danger fw-bold"
          onClick={() => setInputValue('')}
          title="Clear search"
        >
          ✕
        </button>

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm ms-1"
          {...getToggleButtonProps()}
        >
          {isOpen ? '▲' : '▼'}
        </button>
      </div>

      {typeof document !== 'undefined' && menuStyle ? createPortal(menu, document.body) : menu}
    </div>
  );
}
